export const circledNumber = (num: number): string => {
  switch (num) {
    case 1:
      return '①'
    case 2:
      return '②'
    case 3:
      return '③'
    case 4:
      return '④'
    case 5:
      return '⑤'
    case 6:
      return '⑥'
    case 7:
      return '⑦'
    case 8:
      return '⑧'
    case 9:
      return '⑨'
  }
  return ''
}

export const numberToAlphabet = (num: number): string => {
  // 1→A, 2→B ... 26→Z
  return String.fromCharCode(64 + num)
}

export const zeroPadding = (num: number, length: number): string => {
  return (Array(length).join('0') + num).slice(-length)
}

export const commaSeparatedNumber = (value: number) => {
  return Math.trunc(value).toLocaleString('ja-JP')
}

const KANJI_DIGIT_MAP: Record<string, number> = {
  〇: 0,
  零: 0,
  一: 1,
  二: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
  壱: 1,
  弐: 2,
  参: 3
}

const KANJI_UNIT_MAP: Record<string, number> = {
  十: 10,
  百: 100,
  千: 1000,
  万: 10000,
  億: 100000000,
  兆: 1000000000000
}

const isAsciiDigit = (char: string): boolean => {
  const code = char.charCodeAt(0)
  return code >= 48 && code <= 57
}

const isFullWidthDigit = (char: string): boolean => {
  const code = char.charCodeAt(0)
  return code >= 0xff10 && code <= 0xff19
}

const normalizeDigit = (char: string): string => {
  if (isAsciiDigit(char)) {
    return char
  }
  if (isFullWidthDigit(char)) {
    return String.fromCharCode(char.charCodeAt(0) - 0xff10 + 48)
  }
  return ''
}

const isKanjiNumberChar = (char: string): boolean => {
  return (
    Object.prototype.hasOwnProperty.call(KANJI_DIGIT_MAP, char) ||
    Object.prototype.hasOwnProperty.call(KANJI_UNIT_MAP, char)
  )
}

const isNumericChar = (char: string): boolean => {
  return isAsciiDigit(char) || isFullWidthDigit(char) || isKanjiNumberChar(char)
}

const kanjiNumberToArabic = (value: string): string => {
  if (!value) {
    return ''
  }

  let total = 0
  let section = 0
  let current = 0

  for (const char of value) {
    if (Object.prototype.hasOwnProperty.call(KANJI_DIGIT_MAP, char)) {
      current = KANJI_DIGIT_MAP[char]
      continue
    }

    const unit = KANJI_UNIT_MAP[char]
    if (unit >= 10000) {
      if (current === 0 && section === 0) {
        section = 1
      } else {
        section += current
      }
      total += section * unit
      section = 0
      current = 0
    } else if (unit) {
      if (current === 0) {
        current = 1
      }
      section += current * unit
      current = 0
    }
  }

  section += current
  total += section

  return String(total)
}

/**
 * 住所文字列中の漢数字を数字に変換し、
 * 初めての数字以降の非数字文字列をハイフンに正規化する。
 *
 * 例:
 *   "東京都千代田区一丁目二番三号" -> "東京都千代田区1-2-3-"
 */
export const convertAddressKanjiToNumber = (value: string): string => {
  if (!value) {
    return value
  }

  let firstNumericIndex = -1
  for (let i = 0; i < value.length; i++) {
    if (isNumericChar(value[i])) {
      firstNumericIndex = i
      break
    }
  }

  if (firstNumericIndex === -1) {
    return value
  }

  let result = value.slice(0, firstNumericIndex)
  let i = firstNumericIndex
  let lastWasHyphen = false

  while (i < value.length) {
    const char = value[i]

    if (isAsciiDigit(char) || isFullWidthDigit(char)) {
      result += normalizeDigit(char)
      lastWasHyphen = false
      i += 1
      continue
    }

    if (isKanjiNumberChar(char)) {
      let segment = ''
      while (i < value.length && isKanjiNumberChar(value[i])) {
        segment += value[i]
        i += 1
      }
      const numeric = kanjiNumberToArabic(segment)
      if (numeric !== '') {
        result += numeric
        lastWasHyphen = false
      }
      continue
    }

    if (!lastWasHyphen) {
      result += '-'
      lastWasHyphen = true
    }
    i += 1
  }

  if (lastWasHyphen && result.endsWith('-')) {
    return result.slice(0, -1)
  }

  return result
}

export const formatPercent = (n: number | null | undefined) =>
  n != null ? (n * 100).toFixed(2) + '%' : '0.00%'

/**
 * 符号付きの小数表記。増減や正負が意味を持つスコアの表示に使う。
 *
 * 例:
 *   signedFixed(0.35)  -> "+0.35"
 *   signedFixed(-0.3)  -> "-0.30"
 *   signedFixed(null)  -> "-"
 */
export const signedFixed = (
  value: number | null | undefined,
  fractionDigits: number = 2
): string => (value == null ? '-' : `${value > 0 ? '+' : ''}${value.toFixed(fractionDigits)}`)

/**
 * 1万以上の数値を千単位（K）で省略表記する。
 * 1万未満はそのままカンマ区切りで返す。
 *
 * 例:
 *   999     -> "999"
 *   9999    -> "9,999"
 *   10000   -> "10K"
 *   12345   -> "12.3K"
 *   1500000 -> "1,500K"
 */
export const abbreviateNumber = (value: number): string => {
  const truncated = Math.trunc(value)
  if (Math.abs(truncated) < 10000) {
    return truncated.toLocaleString('ja-JP')
  }
  const thousands = truncated / 1000
  // 小数第1位まで（末尾の .0 は除去）
  const rounded = Math.round(thousands * 10) / 10
  return rounded.toLocaleString('ja-JP') + 'K'
}
